import { z } from 'zod';
import { defineRoute } from './route.ts';
import { ENGINES, ERROR_KINDS, REPORTED_OUTCOMES } from './probe-values.ts';

/**
 * 워커 ↔ 서비스 계약 (/internal/worker/*). 워커는 이 계약만 알고 DB·GCP 자격증명이 없다.
 * 260915-probe-worker-pool — HTTP 짧은 폴링. 모든 요청은 즉시 응답한다(롱폴링 금지).
 */

export const Egress = z.object({
  ip: z.string().nullable(),
  asn: z.string().nullable(),
  asnOrg: z.string().nullable(),
  country: z.string().nullable(),
  city: z.string().nullable(),
});
export type Egress = z.infer<typeof Egress>;

/** 워커가 도는 기계의 자원 — VM 크기를 감이 아니라 실측으로 정하기 위해 (admin 전용으로 표시) */
export const WorkerResources = z.object({
  cpuCount: z.number().int(),
  memTotalMB: z.number().int(),
  memUsedMB: z.number().int(),
  load1: z.number(),
});
export type WorkerResources = z.infer<typeof WorkerResources>;

export const WorkerRuntime = z.object({
  host: z.string(),
  version: z.string(),
  probeVersion: z.string(),
  chromeVersion: z.string().nullable(),
  reportedEgress: Egress.nullable(),
  resources: WorkerResources.nullable(),
});

/**
 * 자기 등록 — 아직 워커 자격증명이 없는 기계가 등록 토큰(`gwe_…`)으로 **자기 워커와 토큰**을 받는다.
 * (260918-worker-self-enrollment)
 *
 * ★ 경로가 `/internal/worker/*` 아래가 **아니다.** 그 아래는 전부 workerAuthMiddleware 가 가로채고,
 *   `gwe_` 는 워커 토큰이 아니라 Google ID 토큰 검증으로 떨어져 등록마다 검증 실패 warn 을 남긴다.
 *   자격증명 종류가 다르면 경로를 가른다 — `/internal/scheduler/*` 와 같은 처리다.
 *
 * ★ 네트워크를 보지 않는다. `enabled` 는 등록 토큰의 `auto_enable` 그대로다 — 어디에 둘지는 인프라의 몫이고
 *   시스템은 관측해서 경고만 한다. 요청 IP 로 판정하면 컨테이너의 실제 egress 와 갈려 조용히 틀린다
 */
export const enroll = defineRoute({
  method: 'post',
  path: '/internal/enroll',
  access: 'enroll',
  summary: '등록 토큰으로 워커 한 대를 발급받는다 — 워커 토큰 원문은 이 응답에서 한 번만',
  body: z.object({ hostname: z.string().trim().min(1).max(64) }).strict(),
  response: z.object({
    workerId: z.uuid(),
    name: z.string(),
    token: z.string(),
    enabled: z.boolean(),
  }),
});

export const heartbeat = defineRoute({
  method: 'post',
  path: '/internal/worker/heartbeat',
  access: 'worker',
  summary: '생존 신호·등록 겸함. 진행 중 작업이 아직 내 것인지 확인한다',
  body: z.object({
    bootId: z.uuid(),
    runtime: WorkerRuntime,
    works: z.array(z.object({ workId: z.uuid(), generation: z.number().int() })),
  }),
  response: z.object({
    cancelled: z.array(z.uuid()),
    policy: z.object({ enabled: z.boolean(), minGapSec: z.number().int() }),
    networkLeader: z.boolean(),
    /**
     * ChatGPT 이외 레인의 정책 (261104-gemini-concurrent-lane). `policy` 는 ChatGPT 레인 그대로다.
     * optional — 옛 서비스로 되돌려도 새 워커가 응답을 거부하지 않게. 없으면 그 레인은 꺼진 것으로 본다
     */
    lanes: z.array(z.object({ engine: z.enum(ENGINES), enabled: z.boolean(), minGapSec: z.number().int() })).optional(),
  }),
});

export const Work = z.object({
  workId: z.uuid(),
  /** claim 이 받은 엔진의 되돌림. optional — 옛 서비스는 싣지 않는다(그때는 ChatGPT 다) */
  engine: z.enum(ENGINES).optional(),
  generation: z.number().int(),
  query: z.string(),
  watchdogSec: z.number().int(),
  rawFiles: z.array(z.string()),
});
export type Work = z.infer<typeof Work>;

export const claim = defineRoute({
  method: 'post',
  path: '/internal/worker/claim',
  access: 'worker',
  summary: '일 하나를 점유하거나, 다음에 물어볼 시각을 받는다',
  body: z.object({
    bootId: z.uuid(),
    /**
     * 이 레인의 엔진. ★ 없으면 `chatgpt-web` 이다 — 엔진을 모르는 구버전 워커가 Gemini 일을 받아
     *   ChatGPT 에 던지는 길을 구조로 막는다 (261104-gemini-concurrent-lane)
     */
    engine: z.enum(ENGINES).optional(),
  }),
  response: z.object({
    work: Work.nullable(),
    nextPollAfterSec: z.number().int().nullable(),
    reason: z.string().nullable(),
  }),
});

export const ResultCitation = z.object({
  position: z.number().int().min(1),
  url: z.string(),
  host: z.string(),
  title: z.string().nullable(),
  attribution: z.string().nullable(),
});
export const ResultEntity = z.object({
  position: z.number().int().min(1),
  name: z.string(),
  websiteHost: z.string().nullable(),
});

export const result = defineRoute({
  method: 'post',
  path: '/internal/worker/result',
  access: 'worker',
  summary: '샘플 결과. 원문만 보낸다 — 언급·우리 사이트 판정은 서비스가 조회 시 한다',
  body: z.object({
    bootId: z.uuid(),
    workId: z.uuid(),
    generation: z.number().int(),
    outcome: z.enum(REPORTED_OUTCOMES),
    errorKind: z.enum(ERROR_KINDS).nullable(),
    acceptLanguage: z.string().nullable(),
    answerText: z.string().nullable(),
    egress: Egress.nullable(),
    probeVersion: z.string(),
    chromeVersion: z.string().nullable(),
    rawUploaded: z.array(z.string()),
    citations: z.array(ResultCitation),
    entities: z.array(ResultEntity),
    diagnostics: z.record(z.string(), z.unknown()),
  }),
  response: z.object({ status: z.enum(['applied', 'late', 'duplicate']) }),
});
export type ResultBody = z.infer<typeof result.body>;

export const shutdown = defineRoute({
  method: 'post',
  path: '/internal/worker/shutdown',
  access: 'worker',
  summary: 'graceful 종료 — 점유를 즉시 반납한다',
  body: z.object({ bootId: z.uuid(), reason: z.string().nullable() }),
  response: z.object({ ok: z.literal(true) }),
});

/** raw 업로드는 바이너리라 zod 본문이 없다 — PUT {path}/{workId}/{file} */
export const RAW_PATH = '/internal/worker/raw/{workId}/{file}' as const;

export const maintenance = defineRoute({
  method: 'post',
  path: '/internal/scheduler/maintenance',
  access: 'scheduler',
  summary: '시간 구동 정리 — 전 워커 회수·슬롯 보장·재계산',
  response: z.object({ steps: z.record(z.string(), z.unknown()) }),
});

/**
 * 기사 일괄 작성 — 대기열에서 하나를 쓴다(261007-monthly-article-board). **maintenance 와 다른 스케줄러 작업**이다:
 * 기사 한 편이 1~2분이라 maintenance(마감 120s)에 넣으면 정리 단계 전체가 시간 초과로 죽는다.
 */
export const articleBatch = defineRoute({
  method: 'post',
  path: '/internal/scheduler/article-batch',
  access: 'scheduler',
  summary: '기사 일괄 작성 — 다음 하나',
  response: z.object({ ran: z.boolean(), remaining: z.number().int() }),
});
